// src/components/admin/AdminLekhiPlans.js
import React, { useState, useEffect } from 'react';
import {
  LEKHI_TOPICS,
  computeLekhiRotation,
  getLekhiDailyPlansRange,
  getSavedLekhiDays,
  getAvailableLekhiContentCounts,
  saveLekhiDailyPlansBatch
} from '../../services/firebase/firestoreService';

const SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];
const PAGE_SIZE = 20;
const TOTAL_DAYS = 100;
const TOTAL_PAGES = TOTAL_DAYS / PAGE_SIZE;

const COUNT_FIELDS = [
  { key: 'audio', label: 'Audio' },
  { key: 'text', label: 'Text' },
  { key: 'mcqs', label: 'MCQ' },
  { key: 'fillBlanks', label: 'FillBlank' },
  { key: 'matchFollowing', label: 'Match' },
  { key: 'flashcards', label: 'Flashcard' }
];

export default function AdminLekhiPlans() {
  const [page, setPage] = useState(1); // 1..5
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableCounts, setAvailableCounts] = useState(null); // bulk read once
  const [rows, setRows] = useState([]); // 20 rows for current page
  const [savedDays, setSavedDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  // Bulk-read available counts ONCE per session
  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const counts = await getAvailableLekhiContentCounts();
        if (!cancelled) setAvailableCounts(counts);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    loadCounts();
    return () => { cancelled = true; };
  }, []);

  // Load saved days list (once on mount, refreshed after each save)
  const reloadSavedDays = async () => {
    try {
      const days = await getSavedLekhiDays();
      setSavedDays(days);
    } catch (e) {}
  };

  useEffect(() => {
    reloadSavedDays();
  }, []);

  // Load the rows for the current page
  useEffect(() => {
    if (!availableCounts) return;
    let cancelled = false;
    const loadPage = async () => {
      setLoading(true);
      setSaveResult(null);
      try {
        const startDay = (page - 1) * PAGE_SIZE + 1;
        const endDay = page * PAGE_SIZE;
        const saved = await getLekhiDailyPlansRange(startDay, endDay);

        const builtRows = [];
        for (let i = 0; i < PAGE_SIZE; i++) {
          const dayNum = startDay + i;
          const savedRow = saved[i];
          if (savedRow) {
            builtRows.push({
              day: dayNum,
              subject: savedRow.subject,
              topic: savedRow.topic,
              isRevision: savedRow.isRevision || dayNum > 60,
              counts: {
                audio: savedRow.counts?.audio ?? 0,
                text: savedRow.counts?.text ?? 0,
                mcqs: savedRow.counts?.mcqs ?? 0,
                fillBlanks: savedRow.counts?.fillBlanks ?? 0,
                matchFollowing: savedRow.counts?.matchFollowing ?? 0,
                flashcards: savedRow.counts?.flashcards ?? 0
              },
              isSaved: true
            });
          } else {
            // Auto-fill from rotation + defaults from available content
            const rotation = computeLekhiRotation(dayNum);
            const defaults = getDefaultsForSlice(availableCounts, rotation.subject, rotation.topic);
            builtRows.push({
              day: dayNum,
              subject: rotation.subject,
              topic: rotation.topic,
              isRevision: rotation.isRevision,
              counts: defaults,
              isSaved: false
            });
          }
        }
        if (!cancelled) setRows(builtRows);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPage();
    return () => { cancelled = true; };
  }, [page, availableCounts]);

  // For a (subject, topic), sum counts across all 3 levels in available content
  // and return default counts (max available).
  // Plans don't pick level — user's stored level picks level at runtime.
  // So defaults = max across all levels, which represents max possible content.
  const getDefaultsForSlice = (counts, subject, topic) => {
    if (!counts?.[subject]?.[topic]) {
      return { audio: 0, text: 0, mcqs: 0, fillBlanks: 0, matchFollowing: 0, flashcards: 0 };
    }
    const levels = counts[subject][topic];
    const result = { audio: 0, text: 0, mcqs: 0, fillBlanks: 0, matchFollowing: 0, flashcards: 0 };
    ['easy', 'medium', 'hard'].forEach(l => {
      COUNT_FIELDS.forEach(f => {
        result[f.key] = Math.max(result[f.key], levels[l]?.[f.key] || 0);
      });
    });
    return result;
  };

  const updateRow = (rowIndex, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      if (field === 'subject') {
        row.subject = value;
        row.topic = LEKHI_TOPICS[value][0];
        row.counts = getDefaultsForSlice(availableCounts, row.subject, row.topic);
      } else if (field === 'topic') {
        row.topic = value;
        row.counts = getDefaultsForSlice(availableCounts, row.subject, row.topic);
      } else {
        // count field
        const n = parseInt(value, 10);
        row.counts = { ...row.counts, [field]: isNaN(n) || n < 0 ? 0 : n };
      }
      next[rowIndex] = row;
      return next;
    });
    setSaveResult(null);
  };

  const handleSavePage = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const plans = rows.map(r => ({
        day: r.day,
        subject: r.subject,
        topic: r.topic,
        isRevision: r.isRevision,
        counts: r.counts
      }));
      const n = await saveLekhiDailyPlansBatch(plans);
      setSaveResult({ success: true, message: `Saved ${n} daily plans for page ${page}.` });
      setRows(prev => prev.map(r => ({ ...r, isSaved: true })));
      await reloadSavedDays();
    } catch (e) {
      setSaveResult({ success: false, error: e.message });
    } finally {
      setSaving(false);
    }
  };

  const startDay = (page - 1) * PAGE_SIZE + 1;
  const endDay = page * PAGE_SIZE;

  return (
    <div>
      <div className="admin-section-title">Daily Plans</div>

      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>Days {startDay} – {endDay}</h3>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(p => (
              <button key={p}
                onClick={() => setPage(p)}
                disabled={loading || saving}
                style={{
                  padding: '6px 14px',
                  background: p === page ? '#007fff' : '#fff',
                  color: p === page ? '#fff' : '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '13px'
                }}>
                {(p - 1) * PAGE_SIZE + 1}–{p * PAGE_SIZE}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="admin-status-msg error">Error: {error}</div>
        )}

        {!availableCounts && !error && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Loading available content counts...</div>
        )}

        {availableCounts && loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Loading page...</div>
        )}

        {availableCounts && !loading && rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '850px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left', background: '#f8f9fa' }}>
                    <th style={{ padding: '8px' }}>Day</th>
                    <th style={{ padding: '8px' }}>Subject</th>
                    <th style={{ padding: '8px' }}>Topic</th>
                    {COUNT_FIELDS.map(f => (
                      <th key={f.key} style={{ padding: '8px', textAlign: 'center' }}>{f.label}</th>
                    ))}
                    <th style={{ padding: '8px' }}>Saved?</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.day} style={{ borderBottom: '1px solid #f1f3f5', background: row.isRevision ? '#fffaf0' : '#fff' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{row.day}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={row.subject}
                          onChange={e => updateRow(idx, 'subject', e.target.value)}
                          style={{ fontSize: '12px', padding: '4px' }}>
                          {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={row.topic}
                          onChange={e => updateRow(idx, 'topic', e.target.value)}
                          style={{ fontSize: '12px', padding: '4px' }}>
                          {LEKHI_TOPICS[row.subject].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      {COUNT_FIELDS.map(f => (
                        <td key={f.key} style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <input type="number" min="0" value={row.counts[f.key]}
                            onChange={e => updateRow(idx, f.key, e.target.value)}
                            style={{ width: '50px', padding: '4px', fontSize: '12px', textAlign: 'center' }} />
                        </td>
                      ))}
                      <td style={{ padding: '6px 8px', fontSize: '12px', color: row.isSaved ? '#28a745' : '#999' }}>
                        {row.isSaved ? '✓' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '16px' }}>
              <button className="admin-btn admin-btn-primary"
                onClick={handleSavePage} disabled={saving}>
                {saving ? 'Saving...' : `Save Days ${startDay}–${endDay}`}
              </button>

              {saveResult && (
                <div className={`admin-status-msg ${saveResult.success ? 'success' : 'error'}`}>
                  {saveResult.success ? `✓ ${saveResult.message}` : `Error: ${saveResult.error}`}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="admin-card">
        <h3>Saved Days</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
          {savedDays.length} of {TOTAL_DAYS} days saved.
        </p>
        {savedDays.length === 0 ? (
          <p style={{ color: '#999', fontSize: '14px' }}>No plans saved yet.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {savedDays.map(d => (
              <span key={d} style={{
                padding: '4px 10px',
                background: '#e6f2ff',
                color: '#007fff',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600
              }}>
                Day {d}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}